/**
 * The alert inbox's arithmetic and vocabulary — WP-L5 (Task 53).
 *
 * Pure, no React, for the reason `web/src/devices/fleet.ts` gives.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  TWO SEVERITY VOCABULARIES, ONE EXHAUSTIVE MAP
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The wire's `Severity` is `informational | low | medium | high | critical`; the
 * design system's (`DESIGN.md` §4, `web/src/states/icons.tsx`) is
 * `info | low | medium | high | critical`. They differ in exactly one name.
 *
 * The bridge is an exhaustive `Record`, not a `?? 'info'` fallback. A sixth severity
 * on the wire is then a **compile error** here rather than a badge that silently
 * renders the quietest possible thing about the loudest possible event — which is
 * the direction a fallback fails in, and it fails silently.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  WITHHELD IS NOT ZERO
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `measured_value` and `threshold` are `number | null` on the wire, and `null` means
 * the server did not publish a number — not that the number is zero. D-22 shipped a
 * withheld overtime figure rendered as "0 giờ" on the employee's own screen because
 * a nullable field was typed `number | undefined` and `?? 0` looked reasonable.
 *
 * So the reading is a **tagged union** rather than a nullable number: there is no
 * `.value` to read on the withheld branch, so `?? 0` does not type-check, and the
 * confusion is impossible rather than merely discouraged.
 */

import type { Severity } from '../states/icons';
import type { AlertSeverity, AlertStatus, AlertSummary, AlertTrigger } from '../api/types';

/** The wire's five, in the ladder's own order — loudest last, as Master §15.2 has it. */
export const ALERT_SEVERITIES: readonly AlertSeverity[] = [
  'informational',
  'low',
  'medium',
  'high',
  'critical',
];

/** Exhaustive. See the module header for why this is not a fallback. */
export const BADGE_SEVERITY: Readonly<Record<AlertSeverity, Severity>> = {
  informational: 'info',
  low: 'low',
  medium: 'medium',
  high: 'high',
  critical: 'critical',
};

/** Loudest first, which is the order a queue is read in. */
export function bySeverityThenNewest(left: AlertSummary, right: AlertSummary): number {
  const rank = ALERT_SEVERITIES.indexOf(right.severity) - ALERT_SEVERITIES.indexOf(left.severity);
  if (rank !== 0) return rank;
  return right.triggered_at.localeCompare(left.triggered_at);
}

/**
 * The four triggers the Data Risk page is about.
 *
 * `blocked_app` is spelled with `app`, not `application`: `wp_alert_rules`' CHECK is
 * what the database enforces, and an enum that admits a value the INSERT refuses is a
 * 500 waiting for its first caller. Task 49 corrected `openapi.yaml` to match, and
 * this list is a transcription of the corrected one.
 */
export const DATA_RISK_TRIGGERS: readonly AlertTrigger[] = [
  'usb_unapproved',
  'bulk_file_operation',
  'blocked_website',
  'blocked_app',
];

/** A number the server published, or the fact that it did not. No third case. */
export type Reading =
  { readonly kind: 'known'; readonly value: number } | { readonly kind: 'withheld' };

/**
 * `null` and a missing member both become `withheld`.
 *
 * `typeof value !== 'number'` rather than `value === null`, deliberately: a body that
 * omitted the member entirely would pass a `=== null` check as `{ kind: 'known',
 * value: undefined }` and print "undefined" into a queue row. `@typescript-eslint`
 * flagged the same shape on `pending_local_events` in Task 45, and it was right then.
 */
export function readingOf(value: number | null | undefined): Reading {
  return typeof value === 'number' && Number.isFinite(value)
    ? { kind: 'known', value }
    : { kind: 'withheld' };
}

/** One trigger's tile on the Data Risk page. Counts are over what was **read**. */
export interface TriggerCensus {
  readonly trigger: AlertTrigger;
  readonly read: number;
  readonly unacknowledged: number;
  /** The loudest severity among the rows read, or `null` when none were. */
  readonly worst: AlertSeverity | null;
}

/**
 * Count one trigger's rows.
 *
 * `read` rather than `total` in the name, and it is not a stylistic choice:
 * `GET /alerts` is cursor-paginated and its envelope carries **no total at all**. A
 * field called `total` here would be a company-wide claim the server never made, and
 * the screen would print it beside a filter that changed it.
 *
 * `unacknowledged` counts `status === 'open'` and nothing else. A `suppressed` row is
 * a real firing recorded as a duplicate of the alert already standing; counting it as
 * unowned would report the same exposure twice and send somebody to acknowledge a
 * shadow, which the server answers `409`.
 */
export function censusFor(trigger: AlertTrigger, rows: readonly AlertSummary[]): TriggerCensus {
  let unacknowledged = 0;
  let worst: AlertSeverity | null = null;
  for (const row of rows) {
    if (row.status === 'open') unacknowledged += 1;
    if (
      worst === null ||
      ALERT_SEVERITIES.indexOf(row.severity) > ALERT_SEVERITIES.indexOf(worst)
    ) {
      worst = row.severity;
    }
  }
  return { trigger, read: rows.length, unacknowledged, worst };
}

/**
 * Is there an acknowledge control on this row at all?
 *
 * Three answers, and they are three because two of them look identical in a colour-
 * only design and mean opposite things:
 *
 *  - `offer`      — `open`, and the caller holds `resolve_incidents`.
 *  - `done`       — already acknowledged. The row shows **who** and **when** and no
 *                   control: `wp_alert_events_guard()` refuses every UPDATE to an
 *                   acknowledged row, so there is nothing a button could do.
 *  - `suppressed` — a duplicate occurrence. Acknowledging it is `409`, and the alert
 *                   it duplicates is the one on the queue.
 *  - `no-permission` — a reader with `view_department` but not `resolve_incidents`.
 *
 * Nothing here returns "render it disabled". A greyed control asserts "the system
 * will not let you"; for `done` the truth is "there is nothing left to do", and for
 * `suppressed` it is "you want the other row".
 */
export type AcknowledgeAffordance = 'offer' | 'done' | 'suppressed' | 'no-permission';

export function acknowledgeAffordance(
  status: AlertStatus,
  acknowledgedAt: string | null,
  mayResolve: boolean,
): AcknowledgeAffordance {
  if (status === 'suppressed') return 'suppressed';
  if (acknowledgedAt !== null || status === 'acknowledged') return 'done';
  if (!mayResolve) return 'no-permission';
  return 'offer';
}
